import { Link, useNavigate } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { api } from "../../convex/_generated/api"
import { toast } from "sonner"
import { authClient, roleHome, signOutSucceeded } from "@/lib/auth-client"
import { Button } from "@/components/ui/button"
import { NotificationBell } from "@/components/notification-bell"
import { Logo } from "@/components/logo"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

const activeNavProps = {
  className:
    "bg-accent text-accent-foreground hover:bg-accent hover:text-accent-foreground",
}

/** Forest chrome for admin / legacy desks only (candidate + recruit use ink headers). */
export function SiteHeader() {
  const profile = useQuery(api.profiles.me)
  const { data: session } = authClient.useSession()
  const navigate = useNavigate()
  const loggedIn = !!session?.user

  return (
    <header className="sticky top-0 z-40 border-b border-border/80 bg-background/80 backdrop-blur">
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-4 px-4">
        <Link
          to={profile ? roleHome(profile.role) : "/"}
          className="inline-flex items-center text-primary"
          aria-label="Hire home"
        >
          <Logo />
        </Link>
        <nav className="flex items-center gap-1 sm:gap-2 text-sm">
          {loggedIn ? (
            <>
              {profile?.role === "seeker" && (
                <>
                  <Button variant="ghost" size="sm" asChild>
                    <Link
                      to="/candidate"
                      activeOptions={{ exact: true }}
                      activeProps={activeNavProps}
                    >
                      Discover
                    </Link>
                  </Button>
                  <Button variant="ghost" size="sm" asChild>
                    <Link
                      to="/candidate/applications"
                      activeProps={activeNavProps}
                    >
                      Applications
                    </Link>
                  </Button>
                  <Button variant="ghost" size="sm" asChild>
                    <Link
                      to="/candidate/messages"
                      search={{ thread: undefined }}
                      activeProps={activeNavProps}
                    >
                      Messages
                    </Link>
                  </Button>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/candidate/settings" activeProps={activeNavProps}>
                      Settings
                    </Link>
                  </Button>
                </>
              )}
              {profile?.role === "employer" && (
                <>
                  <Button variant="ghost" size="sm" asChild>
                    <Link
                      to="/recruit"
                      activeOptions={{ exact: true }}
                      activeProps={activeNavProps}
                    >
                      Dashboard
                    </Link>
                  </Button>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/recruit/jobs" activeProps={activeNavProps}>
                      Jobs
                    </Link>
                  </Button>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/recruit/talent" activeProps={activeNavProps}>
                      Find Talent
                    </Link>
                  </Button>
                  <Button variant="ghost" size="sm" asChild>
                    <Link
                      to="/recruit/messages"
                      search={{ thread: undefined }}
                      activeProps={activeNavProps}
                    >
                      Messages
                    </Link>
                  </Button>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/recruit/hackathons" activeProps={activeNavProps}>
                      Hackathons
                    </Link>
                  </Button>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/recruit/settings" activeProps={activeNavProps}>
                      Settings
                    </Link>
                  </Button>
                </>
              )}
              {profile === null && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm">Finish signup</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link to="/jobs/join">Go to Candidate</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/recruit/join">Go to Recruiter</Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {profile && (
                <NotificationBell
                  viewAllPath={
                    profile.role === "employer"
                      ? "/recruit/notifications"
                      : "/candidate/notifications"
                  }
                />
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (!(await signOutSucceeded())) {
                    toast.error("Could not sign out. Please try again.")
                    return
                  }
                  void navigate({ to: "/" })
                }}
              >
                Sign out
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" asChild>
                <Link to="/login">Log in</Link>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm">Join</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/jobs/join">Candidate join</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/recruit/join">Recruiter join</Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </nav>
      </div>
    </header>
  )
}
